import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { Plus, Filter, Download, BarChart3, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import DataTable from '@/components/DataTable';
import AppliedFilters from '@/components/AppliedFilters';
import PqrFormModal from '@/components/PQRs/PqrFormModal';
import PqrDetailModal from '@/components/PQRs/PqrDetailModal';
import PqrMetricsModal from '@/components/PQRs/PqrMetricsModal';
import PqrManageModal from '@/components/PQRs/PqrManageModal';
import PqrViewModal from '@/components/PQRs/PqrViewModal';
import { pqrsColumns, columnsExcel } from '@/components/PQRs/pqrsColumns';
import pqrsService from '@/services/pqrs.service';
import { fetchEdificioById } from '@/services/edificios.service';
import ExportExcelButton from '@/components/ExportExcelButton';
import { uploadDocumentFlow } from '@/services/documentsService';

const PQRsPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [pqrs, setPqrs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedPqr, setSelectedPqr] = useState(null);
  const [selectedManagePqr, setSelectedManagePqr] = useState(null);
  const [selectedViewPqr, setSelectedViewPqr] = useState(null);
  const [editingPqr, setEditingPqr] = useState(null);

  useEffect(() => {
    loadPqrs();
  }, [filters, pagination.page]);

  const loadPqrs = async () => {
    setLoading(true);
    try {
      const response = await pqrsService.fetchPqrs({
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      });

      if (response.success) {
        setPqrs(response.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.total || 0
        }));
      }
    } catch (error) {
      console.error('Error al cargar PQRs:', error);
      toast({
        title: "Error",
        description: "Error al cargar las PQRs",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleRemoveFilter = (filterKey) => {
    const newFilters = { ...filters };
    delete newFilters[filterKey];
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({});
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleModalSubmit = async (formData) => {
    setLoading(true);
    try {
      // Upload images first
      let adjuntos = [];
      const selectedImages = formData.selectedImages || [];
      if (selectedImages.length > 0) {
        try {
          const uploadPromises = selectedImages.map(file =>
            uploadDocumentFlow({
              ownerType: 'pqr',
              ownerId: 'temp_' + Date.now(),
              docType: 'imagen',
              file,
            })
          );
          const uploadResults = await Promise.all(uploadPromises);
          adjuntos = uploadResults.map(result => ({
            id: result.id || result._id,
            nombre: result.fileName || result.meta?.fileName,
            url: result.url || result.publicUrl,
          }));
        } catch (uploadError) {
          console.error('Error uploading images:', uploadError);
          toast({
            title: 'Advertencia',
            description: 'Algunas imágenes no se pudieron subir',
            variant: 'destructive',
          });
        }
      }

      // Construir objeto origen
      const origen = {
        tipo: formData.origenTipo
      };

      // Siempre incluir propiedad y apartamento si se proporcionaron
      if (formData.edificioId) {
        origen.propiedad = {
          id: formData.edificioId,
          nombre: formData.apartamento || 'Sin especificar',
          edificio: {
            id: formData.edificioId,
            nombre: formData.edificioNombre || '',
            ciudad: formData.edificioCiudad || ''
          }
        };
      }
      if (formData.apartamento) {
        origen.apartamento = formData.apartamento;
      }

      const pqrData = {
        tipo: formData.tipo,
        categoria: formData.categoria,
        origen: origen,
        descripcion: formData.descripcion,
        prioridad: formData.prioridad || 'Media',
        usuarioReporta: {
          id: user.id || 'sistema',
          nombre: formData.reportaNombre,
          correo: formData.reportaCorreo || undefined,
          telefono: formData.reportaTelefono || undefined
        },
        adjuntos: adjuntos.length > 0 ? adjuntos : undefined
      };

      if (editingPqr) {
        const response = await pqrsService.updatePqr(editingPqr._id, {
          ...pqrData,
          usuarioActualizacion: {
            id: user.id,
            nombre: user.name,
            correo: user.email
          }
        });

        if (response.success) {
          toast({
            title: "Éxito",
            description: "PQR actualizada exitosamente",
          });
          loadPqrs();
          setIsFormModalOpen(false);
          setEditingPqr(null);
        }
      } else {
        const response = await pqrsService.createPqr(pqrData);

        if (response.success) {
          toast({
            title: "Éxito",
            description: `PQR creada exitosamente: ${response.data.consecutivo}`,
          });
          loadPqrs();
          setIsFormModalOpen(false);
        }
      }
    } catch (error) {
      console.error('Error al guardar PQR:', error);
      toast({
        title: "Error",
        description: error.message || 'Error al guardar la PQR',
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleManage = (pqr) => {
    setSelectedManagePqr(pqr);
    setIsManageModalOpen(true);
  };

  const handleManageUpdate = () => {
    loadPqrs();
    setIsManageModalOpen(false);
    setSelectedManagePqr(null);
  };

  const handleView = (pqr) => {
    setSelectedViewPqr(pqr);
    setIsViewModalOpen(true);
  };

  const handleExport = () => {
    if (pqrs.length === 0) {
      toast({
        title: "Advertencia",
        description: "No hay datos para exportar",
        variant: "destructive",
      });
      return;
    }

    const dataToExport = pqrs.map(pqr => ({
      consecutivo: pqr.consecutivo,
      tipo: pqr.tipo,
      categoria: pqr.categoria,
      descripcion: pqr.descripcion,
      prioridad: pqr.prioridad,
      estado: pqr.estado,
      'usuarioReporta.nombre': pqr.usuarioReporta?.nombre || '-',
      'responsable.nombre': pqr.responsable?.nombre || '-',
      fechaRegistro: pqr.fechaRegistro,
      fechaCierre: pqr.fechaCierre || '-',
      solucion: pqr.solucion || '-'
    }));

    toast({
      title: "Éxito",
      description: "Preparando exportación...",
    });
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePqrUpdate = () => {
    loadPqrs();
    setIsDetailModalOpen(false);
    setSelectedPqr(null);
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">PQRs</h1>
          <p className="text-gray-500 mt-1">
            Gestión de Peticiones, Quejas y Reclamos
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setIsMetricsModalOpen(true)}
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            Métricas
          </Button>
          <ExportExcelButton
            service={async () => {
              const response = await pqrsService.fetchPqrs({
                ...filters,
                limit: 10000,
                page: 1
              });
              return response.data || [];
            }}
            filters={filters}
            columns={columnsExcel}
            fileName="PQRs"
          />
          <Button onClick={() => {
            setEditingPqr(null);
            setIsFormModalOpen(true);
          }}>
            <Plus className="h-4 w-4 mr-2" />
            Nueva PQR
          </Button>
        </div>
      </div>

      {Object.keys(filters).length > 0 && (
        <AppliedFilters
          filters={filters}
          fields={filterFields}
          onRemoveFilter={handleRemoveFilter}
          onClearFilters={handleClearFilters}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Listado de PQRs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            data={pqrs}
            columns={pqrsColumns}
            loading={loading}
            showDefaultActions={false}
            onAction={(action, row) => {
              if (action === 'manage') {
                handleManage(row);
              } else if (action === 'view') {
                handleView(row);
              }
            }}
            pagination={pagination}
            onPageChange={handlePageChange}
            emptyMessage="No se encontraron PQRs"
          />
        </CardContent>
      </Card>


      <PqrFormModal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setEditingPqr(null);
        }}
        onSubmit={handleModalSubmit}
        initialData={editingPqr}
        loading={loading}
      />

      {selectedPqr && (
        <PqrDetailModal
          isOpen={isDetailModalOpen}
          onClose={() => {
            setIsDetailModalOpen(false);
            setSelectedPqr(null);
          }}
          pqr={selectedPqr}
          onUpdate={handlePqrUpdate}
        />
      )}

      <PqrMetricsModal
        isOpen={isMetricsModalOpen}
        onClose={() => setIsMetricsModalOpen(false)}
        filters={filters}
      />

      {selectedManagePqr && (
        <PqrManageModal
          isOpen={isManageModalOpen}
          onClose={() => {
            setIsManageModalOpen(false);
            setSelectedManagePqr(null);
          }}
          pqr={selectedManagePqr}
          onUpdate={handleManageUpdate}
        />
      )}

      {selectedViewPqr && (
        <PqrViewModal
          isOpen={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedViewPqr(null);
          }}
          pqr={selectedViewPqr}
        />
      )}
    </div>
  );
};

export default PQRsPage;
